export type SettingsScopeType = 'org' | 'project' | 'application';

/** The page a group's defaults are set on, at every scope that has one. */
export type SettingsPageSection = 'ai' | 'voice' | 'privacy';

export interface SettingsGroupEntry {
  section: SettingsPageSection;
  /** The scopes that can set it, as maestro-platform's `Settings.Groups` lists them. */
  scopes: readonly SettingsScopeType[];
  /**
   * The scopes whose settings page shows it as a card. Recording is set per
   * phone number below the org; privacy and live agent are pickers, not cards.
   */
  cards: readonly SettingsScopeType[];
  /** A real group the design has no place for: shown last, flagged for product. */
  checkIn?: boolean;
}

const ALL: readonly SettingsScopeType[] = ['org', 'project', 'application'];

/**
 * Every settings group, in the design's order within each page. Adding a group
 * here, with its spec in `GROUP_SPECS` and its copy, is the whole change;
 * `group-registry.test.ts` holds the backend's list to check it against.
 */
export const SETTINGS_GROUPS: Record<string, SettingsGroupEntry> = {
  llm: { section: 'ai', scopes: ALL, cards: ALL },
  decision_model: { section: 'ai', scopes: ALL, cards: ALL },
  agent_defaults: {
    section: 'ai',
    scopes: ['org', 'project'],
    cards: ['org', 'project'],
    checkIn: true,
  },
  stt: { section: 'voice', scopes: ALL, cards: ALL },
  languages: { section: 'voice', scopes: ALL, cards: ALL },
  conversation: { section: 'voice', scopes: ALL, cards: ALL },
  recording: { section: 'voice', scopes: ['org', 'project'], cards: ['org'] },
  call: { section: 'voice', scopes: ALL, cards: ALL, checkIn: true },
  voice_tuning: { section: 'voice', scopes: ALL, cards: ALL, checkIn: true },
  retention: {
    section: 'privacy',
    scopes: ['org', 'project'],
    cards: ['project'],
  },
  privacy: { section: 'voice', scopes: ['application'], cards: [] },
  live_agent: { section: 'voice', scopes: ['application'], cards: [] },
};

/** The cards a scope's page for `section` shows, in order. */
export const groupsOnPage = (
  scope: SettingsScopeType,
  section: SettingsPageSection
): string[] =>
  Object.entries(SETTINGS_GROUPS)
    .filter(([, g]) => g.section === section && g.cards.includes(scope))
    .map(([group]) => group);

export const checkInGroups = (): string[] =>
  Object.entries(SETTINGS_GROUPS)
    .filter(([, g]) => g.checkIn)
    .map(([group]) => group);

/** An unknown group, one the backend added first, links to the AI page. */
export const sectionOfGroup = (group: string): SettingsPageSection =>
  SETTINGS_GROUPS[group]?.section ?? 'ai';
